/**
 * Video URL helpers + Insurance / Knowledge Hub walkthrough slides.
 *
 * Walkthrough videos support:
 * - YouTube
 * - Vimeo
 * - Direct MP4
 * - Direct HLS (.m3u8)
 *
 * Non-walkthrough video artifacts still require direct MP4/HLS
 * if they use normalizeDirectPlaybackUrl().
 */

const YT_HOSTS = new Set([
  "www.youtube.com",
  "youtube.com",
  "m.youtube.com",
  "youtu.be",
  "www.youtu.be",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
]);

const VIMEO_HOSTS = new Set([
  "vimeo.com",
  "www.vimeo.com",
  "player.vimeo.com",
]);

function isIOSDevice() {
  if (typeof navigator === "undefined") return false;

  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

/**
 * YouTube URL formats supported:
 *
 * https://www.youtube.com/watch?v=XXXXXXXXXXX
 * https://youtube.com/watch?v=XXXXXXXXXXX
 * https://youtu.be/XXXXXXXXXXX
 * https://www.youtube.com/embed/XXXXXXXXXXX
 * https://www.youtube.com/v/XXXXXXXXXXX
 * https://www.youtube.com/shorts/XXXXXXXXXXX
 * https://www.youtube.com/live/XXXXXXXXXXX
 * https://www.youtube-nocookie.com/embed/XXXXXXXXXXX
 */
const YOUTUBE_ID_REGEX =
  /(?:youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|v\/|shorts\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

/**
 * Vimeo URL formats supported:
 *
 * https://vimeo.com/123456789
 * https://vimeo.com/video/123456789
 * https://vimeo.com/channels/channel-name/123456789
 * https://vimeo.com/groups/group-name/videos/123456789
 * https://player.vimeo.com/video/123456789
 */
const VIMEO_ID_REGEX =
  /(?:vimeo\.com\/)(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?(\d+)/;

/**
 * Safely extract hostname.
 */
function safeHostname(url) {
  if (!url || typeof url !== "string") return null;

  try {
    return new URL(url.trim()).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * Check whether URL belongs to YouTube or Vimeo.
 */
function isYouTubeOrVimeoUrl(url) {
  if (!url || typeof url !== "string") return false;

  const trimmed = url.trim();

  if (!trimmed) return false;

  const host = safeHostname(trimmed);

  if (host) {
    if (YT_HOSTS.has(host) || VIMEO_HOSTS.has(host)) {
      return true;
    }

    if (
      host.endsWith(".youtube.com") ||
      host.endsWith(".youtu.be") ||
      host.endsWith(".vimeo.com")
    ) {
      return true;
    }
  }

  /**
   * Fallback for URLs that may not parse normally,
   * but clearly contain YouTube/Vimeo domains.
   */
  const lower = trimmed.toLowerCase();

  return (
    lower.includes("youtube.com") ||
    lower.includes("youtu.be") ||
    lower.includes("vimeo.com")
  );
}

/**
 * Clean URL.
 */
function cleanUrl(url) {
  if (!url || typeof url !== "string") return null;

  const trimmed = url.trim();

  return trimmed && trimmed !== "null" ? trimmed : null;
}

/**
 * Extract a YouTube video ID and build an embed URL.
 */
export function getYouTubeEmbedUrl(url) {
  if (!url || typeof url !== "string") return null;

  const match = url.match(YOUTUBE_ID_REGEX);

  if (!match) return null;

  const mute = isIOSDevice() ? "&mute=1" : "";

  return `https://www.youtube-nocookie.com/embed/${match[1]}?rel=0&modestbranding=1&autoplay=1${mute}&playsinline=1`;
}

/**
 * Extract a Vimeo video ID and build an embed URL.
 */
export function getVimeoEmbedUrl(url) {
  if (!url || typeof url !== "string") return null;

  const match = url.match(VIMEO_ID_REGEX);

  if (!match) return null;

  const muted = isIOSDevice() ? 1 : 0;

  return `https://player.vimeo.com/video/${match[1]}?autoplay=1&muted=${muted}&playsinline=1`;
}

/**
 * Resolve a walkthrough video URL into something playable.
 *
 * Returns:
 *
 * {
 *   type: "youtube" | "vimeo" | "direct",
 *   src: string
 * }
 *
 * or null if the URL is empty/unparseable.
 *
 * Supported:
 * - YouTube
 * - Vimeo
 * - Direct MP4
 * - Direct HLS
 */
export function getWalkthroughEmbed(url) {
  const trimmed = cleanUrl(url);

  if (!trimmed) return null;

  /**
   * YouTube / Vimeo
   */
  if (isYouTubeOrVimeoUrl(trimmed)) {
    const yt = getYouTubeEmbedUrl(trimmed);

    if (yt) {
      return {
        type: "youtube",
        src: yt,
      };
    }

    const vimeo = getVimeoEmbedUrl(trimmed);

    if (vimeo) {
      return {
        type: "vimeo",
        src: vimeo,
      };
    }

    /**
     * Recognized YouTube/Vimeo host,
     * but video ID could not be extracted.
     */
    return null;
  }

  /**
   * Direct MP4/HLS/other direct video URL.
   *
   * We intentionally do not restrict the extension here because
   * some HLS/CDN URLs do not end with .m3u8 or .mp4.
   */
  return {
    type: "direct",
    src: trimmed,
  };
}

/**
 * Playback URL for the shared player / embed.
 *
 * Supports:
 * - YouTube
 * - Vimeo
 * - Direct MP4
 * - Direct HLS
 */
export function normalizePlaybackUrl(videoUrl) {
  const trimmed = cleanUrl(videoUrl);

  if (!trimmed) return null;

  /**
   * YouTube / Vimeo
   */
  if (isYouTubeOrVimeoUrl(trimmed)) {
    const embed = getWalkthroughEmbed(trimmed);

    return embed ? embed.src : null;
  }

  /**
   * Direct MP4 / HLS / CDN video URL
   */
  return trimmed;
}

/**
 * Direct playback URL for native <video> player.
 *
 * YouTube/Vimeo are rejected here because native <video>
 * cannot directly play those URLs.
 *
 * This function is intended for non-walkthrough video artifacts.
 */
export function normalizeDirectPlaybackUrl(videoUrl) {
  const trimmed = cleanUrl(videoUrl);

  if (!trimmed) return null;

  if (isYouTubeOrVimeoUrl(trimmed)) {
    return null;
  }

  return trimmed;
}

/**
 * Admin forms validation for Video Artifacts.
 *
 * Error is returned only when a recognized YouTube/Vimeo URL
 * cannot be parsed into a video ID.
 */
export function rejectExternalEmbedUrl(url) {
  const trimmed = cleanUrl(url);

  if (!trimmed) return null;

  if (isYouTubeOrVimeoUrl(trimmed)) {
    const embed = getWalkthroughEmbed(trimmed);

    if (!embed) {
      return "Couldn't find a video ID in this YouTube/Vimeo URL. Check the link and try again.";
    }

    return null;
  }

  return null;
}

/**
 * Admin forms validation for Walkthrough Videos.
 *
 * Walkthrough videos ACCEPT:
 * - YouTube
 * - Vimeo
 * - Direct MP4
 * - Direct HLS
 *
 * Therefore YouTube/Vimeo links must NOT be rejected here.
 */
export function rejectWalkthroughEmbedUrl(url) {
  const trimmed = cleanUrl(url);

  if (!trimmed) return null;

  /**
   * YouTube and Vimeo are valid walkthrough URLs.
   */
  if (isYouTubeOrVimeoUrl(trimmed)) {
    const embed = getWalkthroughEmbed(trimmed);

    if (!embed) {
      return "Couldn't find a video ID in this YouTube/Vimeo URL. Check the link and try again.";
    }

    return null;
  }

  /**
   * Direct MP4/HLS URLs are also valid.
   */
  return null;
}

// -----------------------------------------------------------------------------
// Walkthrough (IW / KH)
// -----------------------------------------------------------------------------

/**
 * Get walkthrough URLs and text.
 *
 * Knowledge Hub:
 * - knowledgehub_video_url
 * - es_knowledgehub_video_url
 * - knowledgehub_text
 * - es_knowledgehub_text
 *
 * Insurance:
 * - video_url
 * - es_video_url
 * - text
 * - es_text
 */
function getWalkthroughUrls(
  walkthroughVideo,
  { preferKnowledgeHub = false } = {},
) {
  const w = walkthroughVideo || {};

  if (preferKnowledgeHub) {
    return {
      english_url: cleanUrl(w.knowledgehub_video_url),
      spanish_url: cleanUrl(w.es_knowledgehub_video_url),
      text: w.knowledgehub_text,
      es_text: w.es_knowledgehub_text,
    };
  }

  return {
    english_url: cleanUrl(w.video_url),
    spanish_url: cleanUrl(w.es_video_url),
    text: w.text,
    es_text: w.es_text,
  };
}

/**
 * Determine whether walkthrough should be skipped.
 */
function shouldSkipWalkthrough(verification) {
  if (!verification?.isWalkthrough) {
    return true;
  }

  if (
    verification?.isRepull === "true" ||
    verification?.isRepull === true
  ) {
    return true;
  }

  /**
   * FCT / Insurance table does not skip walkthroughs.
   */

  if (verification?.isExpressLogin) {
    return true;
  }

  return false;
}

/**
 * Determine whether Knowledge Hub walkthrough should be included.
 */
function shouldIncludeWalkthrough(verification) {
  if (shouldSkipWalkthrough(verification)) {
    return false;
  }

  if (verification?.hasSeenWalkthroughVideo) {
    return false;
  }

  const urls = getWalkthroughUrls(
    verification?.walkthroughVideoUrl,
    {
      preferKnowledgeHub: true,
    },
  );

  return !!(
    urls.english_url ||
    urls.spanish_url ||
    urls.text ||
    urls.es_text
  );
}

/**
 * Determine whether Insurance walkthrough should be included.
 *
 * Uses hasSeenInsuranceWalkthrough.
 */
export function shouldIncludeInsuranceWalkthrough(verification) {
  if (shouldSkipWalkthrough(verification)) {
    return false;
  }

  if (verification?.hasSeenInsuranceWalkthrough) {
    return false;
  }

  const w = verification?.walkthroughVideoUrl || {};

  return !!(
    (
      typeof w.video_url === "string" &&
      w.video_url.trim()
    ) ||
    (
      typeof w.es_video_url === "string" &&
      w.es_video_url.trim()
    ) ||
    (
      typeof w.text === "string" &&
      w.text.trim()
    ) ||
    (
      typeof w.es_text === "string" &&
      w.es_text.trim()
    )
  );
}

/**
 * Build walkthrough artifact.
 *
 * Walkthrough AVATAR:
 * - plays video using ai_url
 *
 * Walkthrough READ:
 * - shows text
 *
 * ai_url can contain:
 * - YouTube
 * - Vimeo
 * - direct MP4
 * - direct HLS
 */
function buildWalkthroughArtifact(
  verification,
  options = {},
) {
  const {
    force = false,
    preferKnowledgeHub = true,
  } = options;

  /**
   * Normal inclusion rules.
   */
  if (!force) {
    if (
      preferKnowledgeHub &&
      !shouldIncludeWalkthrough(verification)
    ) {
      return null;
    }

    if (
      !preferKnowledgeHub &&
      !shouldIncludeInsuranceWalkthrough(verification)
    ) {
      return null;
    }
  }

  const urls = getWalkthroughUrls(
    verification?.walkthroughVideoUrl,
    {
      preferKnowledgeHub,
    },
  );

  /**
   * Nothing to show.
   */
  if (
    !urls.english_url &&
    !urls.spanish_url &&
    !urls.text &&
    !urls.es_text
  ) {
    return null;
  }

  return {
    id: preferKnowledgeHub
      ? "walkthrough-kh"
      : "walkthrough-insurance",

    artifactId: preferKnowledgeHub
      ? "walkthrough-kh"
      : "walkthrough-insurance",

    __typename: "WalkthroughArtifact",

    isWalkthrough: true,

    walkthroughType: preferKnowledgeHub
      ? "knowledgeHub"
      : "insurance",

    position: -1,

    /**
     * Video URLs.
     *
     * These can be:
     * - YouTube
     * - Vimeo
     * - MP4
     * - HLS
     *
     * The player can resolve them using getWalkthroughEmbed()
     * or normalizePlaybackUrl().
     */
    ai_url: {
      english_url: urls.english_url,
      spanish_url: urls.spanish_url,
    },

    /**
     * READ mode content.
     */
    text: urls.text,
    es_text: urls.es_text,
  };
}

/**
 * Prepend IW and/or KH walkthrough slides
 * before real artifacts.
 */
export function withWalkthroughSlides(
  artifactsArray,
  verification,
  options = {},
) {
  const {
    includeInsurance = false,
    includeKnowledgeHub = true,
    forceInsurance = false,
    forceKnowledgeHub = false,
  } = options;

  const list = Array.isArray(artifactsArray)
    ? [...artifactsArray]
    : [];

  const slides = [];

  /**
   * Check whether an artifact already exists.
   */
  const hasId = (id) =>
    list.some((a) => a?.id === id) ||
    slides.some((a) => a?.id === id);

  /**
   * Insurance Walkthrough.
   */
  if (
    includeInsurance &&
    !hasId("walkthrough-insurance")
  ) {
    const ins = buildWalkthroughArtifact(
      verification,
      {
        preferKnowledgeHub: false,
        force: forceInsurance,
      },
    );

    if (ins) {
      slides.push(ins);
    }
  }

  /**
   * Knowledge Hub Walkthrough.
   */
  if (
    includeKnowledgeHub &&
    !hasId("walkthrough-kh")
  ) {
    const kh = buildWalkthroughArtifact(
      verification,
      {
        preferKnowledgeHub: true,
        force: forceKnowledgeHub,
      },
    );

    if (kh) {
      slides.push(kh);
    }
  }

  /**
   * Put walkthrough slides before real artifacts.
   */
  return slides.length
    ? [...slides, ...list]
    : list;
}

/**
 * Check whether an artifact is a walkthrough artifact.
 */
export function isWalkthroughArtifact(artifact) {
  return !!(
    artifact?.isWalkthrough ||
    artifact?.__typename === "WalkthroughArtifact" ||
    artifact?.id === "walkthrough" ||
    artifact?.id === "walkthrough-kh" ||
    artifact?.id === "walkthrough-insurance"
  );
}